/**
 * Hero_Bereich der Willkommensseite (Req 3).
 *
 * Erste, ohne Scrollen sichtbare Fläche: Logo, Überschrift, erläuternder
 * Kurztext, primärer Mitmachen-CTA und sekundärer Anker-CTA
 * „So funktioniert's". Rein dekoratives Bild-Panel rechts.
 */

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import Diversity1RoundedIcon from "@mui/icons-material/Diversity1Rounded";
import { useTheme } from "@mui/material/styles";
import { useI18n } from "../../i18n/I18nProvider";
import { welcomeColors } from "./welcomeTheme";
import { JoinButton, WelcomeLogo, scrollToAnchor } from "./welcomeShared";

export default function WelcomeHero() {
  const { t } = useI18n();
  const theme = useTheme();
  const colors = welcomeColors(theme.palette.mode);

  return (
    <Container maxWidth="lg" component="section" aria-labelledby="welcome-hero-title">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: { xs: 5, md: 4 },
          alignItems: "center",
          py: { xs: 7, md: 11 },
        }}
      >
        <Box>
          <Chip
            icon={<Diversity1RoundedIcon />}
            label={t("welcome.hero.badge")}
            sx={{
              mb: 3,
              bgcolor: colors.accentSoft,
              color: colors.accentText,
              fontWeight: 700,
              px: 1,
              "& .MuiChip-icon": { color: colors.accentText },
            }}
          />
          <Typography
            id="welcome-hero-title"
            variant="h1"
            component="h1"
            sx={{ fontSize: { xs: 40, sm: 52, md: 62 }, maxWidth: 640 }}
          >
            {t("welcome.hero.titleBefore")}{" "}
            <Box component="span" sx={{ color: "secondary.main" }}>
              {t("welcome.hero.titleAccent")}
            </Box>
          </Typography>
          <Typography
            sx={{
              mt: 3,
              maxWidth: 540,
              color: "text.secondary",
              fontSize: { xs: 17, md: 19 },
              lineHeight: 1.7,
            }}
          >
            {t("welcome.hero.subtitle")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mt: 4 }}
          >
            <JoinButton />
            <Button
              variant="outlined"
              size="large"
              href="#so-gehts"
              onClick={scrollToAnchor}
              sx={{ minHeight: 48, borderColor: colors.border, color: "primary.main" }}
            >
              {t("welcome.hero.howItWorks")}
            </Button>
          </Stack>
          <Stack
            direction="row"
            spacing={1.2}
            alignItems="center"
            sx={{ mt: 3, color: "text.secondary" }}
          >
            <CheckCircleRoundedIcon sx={{ color: "secondary.main", fontSize: 19 }} />
            <Typography variant="body2">{t("welcome.hero.checkNote")}</Typography>
          </Stack>
        </Box>

        {/* Logo-Panel: auch auf Smartphones sichtbar (unter dem Text, nicht
            mehr per display:none ausgeblendet). */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              // Dunkelmodus: neutraler Karten-Ton wie die anderen Boxen —
              // das grüne surfaceSoft wirkt dort nicht (kein echter Grünschimmer).
              bgcolor:
                theme.palette.mode === "dark"
                  ? "background.paper"
                  : colors.surfaceSoft,
              border: `1px solid ${colors.border}`,
              position: "relative",
              overflow: "hidden",
              maxWidth: 480,
            }}
          >
            <WelcomeLogo width={{ xs: 260, md: 420 }} />
          </Paper>
        </Box>
      </Box>
    </Container>
  );
}
